import { MODAL_SHARE } from '@components/App/Modal/constants'
import { Tab } from '@components/App/Modal/Share/Share'
import type { ID } from '@octopus-do/octopus-project-core'
import { LoaderActions } from '@store/loaders'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { managerActions } from '@store/reducers/manager/actions'
import { loadProjectSaga, rollbackProject } from '@store/reducers/manager/sagas'
import { CLOSE_MODAL } from '@storeOld/actions'
import {
  GeneralErrorNotification,
  notificationActionCreators,
} from '@storeOld/reducers/notification'
import { closeModal, openModal } from '@storeOld/reducers/ui'
import { UserActions } from '@storeOld/reducers/user/actions'
import type { Action } from 'redux'
import { LOCATION_CHANGE } from 'redux-first-history'
import { call, put, race, select, take } from 'redux-saga/effects'

import type { DashboardActions } from '../../actions'

// The Share window reads the project from the store, so the project is
// loaded there the way a card click loads it — the card holds the loader —
// but nobody goes to it. Leaving the dashboard or opening another project
// meanwhile drops it.
function* load(id: ID) {
  yield put(LoaderActions.set(id))

  try {
    const { left } = yield race({
      loaded: call(loadProjectSaga, id, true),
      left: take([LOCATION_CHANGE, managerActions.resetProject.type]),
    })

    if (left) {
      yield call(rollbackProject, id)
    }

    return !left
  } catch (error) {
    console.error('Error loading the project to share:', error)

    yield call(rollbackProject, id)
    yield put(GeneralErrorNotification)

    return false
  } finally {
    yield put(LoaderActions.unset(id))
  }
}

// Share from the card menu, right on the dashboard (OCT-5462). Only Access:
// Embed and Export work off the open canvas. Someone with a view-only project
// has no Access tab at all, so they are told instead of shown an empty window.
// The project leaves the store once no window is open — an upgrade window
// opened from inside replaces this one and closes later — or once the user is
// somewhere else: a saga left waiting would reset the project later, under an
// editor showing it. The window worked out its permissions for this project;
// they go with it, so nothing on the dashboard reads another project's.
export function* share({
  payload: { id },
}: ReturnType<typeof DashboardActions.projects.share>) {
  const loaded: boolean = yield call(load, id)

  if (!loaded) {
    return
  }

  const readOnly: boolean = yield select(
    migrationProjectSelector.getProjectLegacyAccessTypeIsReadOnly
  )

  if (readOnly) {
    yield call(rollbackProject, id)
    yield put(
      notificationActionCreators.setCustomNotification({
        type: 'orange-opacity',
        text: 'You can only view this project',
        toastContainerId: 'secondary',
      })
    )

    return
  }

  yield put(
    openModal({
      modalType: MODAL_SHARE,
      modalProps: { activeTab: Tab.Access, accessOnly: true },
    })
  )

  const ended: Action = yield take([CLOSE_MODAL, LOCATION_CHANGE])

  if (ended.type === LOCATION_CHANGE) {
    yield put(closeModal())
  }

  yield call(rollbackProject, id)
  yield put({ type: UserActions.SET_PERMISSIONS, payload: null })
}
